<template>
  <div :class="`task-list ${showMine ? 'my-task-list' : 'global-task-list'}`">
    <task-add
      :is-sidebar-active.sync="isAddNewTaskSidebarActive"
      :is-readonly.sync="isAddNewTaskSidebarInDisplayMode"
      :value.sync="activeRecord"
      :edit-tab="editRecord"
      @regenerate-tasks-to-display="
        postTaskUpdateActions([
          {
            oldTask: activeRecord,
            newTask: $event,
            idx: activeRecordIdx,
          },
        ])
      "
      @reset-is-editing-to-false="resetIsEditingValue"
      :init="init"
    />

    <!-- filter tasks by date modal -->
    <filter-tasks-by-date-modal
      :date-range.sync="dateRange"
      :is-editing.sync="isEditing"
      @reset-is-editing-to-false="resetIsEditingValue"
      @update:date-range="onDateSelect($event)"
    />

    <!-- Table Container Card -->
    <div no-body class="mb-0 container-card">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Search & Controls -->
          <b-col cols="12" md="12">
            <div class="d-flex align-items-center justify-content-between flex-wrap toolbar-container">
              <!-- Left Side: Dropdowns and Search Bar (Expands to fill available space) -->
              <div class="d-flex align-items-center flex-grow-1 flex-wrap my-50 toolbar-left">
                <!-- Show Selector -->
                <div class="d-flex align-items-center mr-1 my-25 flex-shrink-0">
                  <span class="dark-muted-text font-small-3 mr-50 text-nowrap font-weight-medium">Show</span>
                  <v-select
                    v-model="showMine"
                    :options="[
                      { label: 'My Tasks', value: true },
                      { label: 'All Tasks', value: false },
                    ]"
                    :reduce="(option) => option.value"
                    :clearable="false"
                    style="min-width: 140px"
                  />
                </div>

                <!-- Group by Selector -->
                <div class="d-flex align-items-center mr-1 my-25 flex-shrink-0">
                  <span class="dark-muted-text font-small-3 mr-50 text-nowrap font-weight-medium">Group by</span>
                  <v-select
                    v-model="groupBy"
                    :options="[
                      { label: 'Category', value: 'Category' },
                      { label: 'None', value: 'None' },
                    ]"
                    :reduce="(option) => option.value"
                    :clearable="false"
                    style="min-width: 130px"
                  />
                </div>

                <!-- Entity Filter (Full Width) -->
                <div class="my-25 mr-1 filter-wrapper flex-grow-1">
                  <transition name="slide-fade">
                    <entity-filter
                      entity="task"
                      entity-plural-slug="tasks"
                      :entity-field-suggestions="suggestions"
                      :search-query.sync="searchQuery"
                      :selected-queries.sync="selectedQueries"
                      :is-editing.sync="isEditing"
                      @reset-is-editing-to-false="resetIsEditingValue"
                      @onClearFilter="onClearFilter"
                      :init="init"
                    />
                  </transition>
                </div>
              </div>

              <!-- Right Side: Action Buttons -->
              <div class="d-flex align-items-center my-50 flex-shrink-0 toolbar-right">
                <template v-if="groupBy === 'Category'">
                  <b-button
                    variant="outline-secondary"
                    class="mr-1 d-inline-flex align-items-center"
                    @click="expandAllCategories"
                  >
                    <span>Expand all</span>
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    class="mr-1 d-inline-flex align-items-center"
                    @click="collapseAllCategories"
                  >
                    <span>Collapse all</span>
                  </b-button>
                </template>

                <transition name="slide-fade">
                  <div v-if="selectedItems.length !== 0">
                    <!-- More Actions On Selected Items Dropdown -->
                    <b-dropdown
                      class="mr-1"
                      toggle-class="text-decoration-none"
                      variant="outline-secondary"
                      no-caret
                      right
                    >
                      <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" size="16" />
                      </template>
                      <b-dropdown-item
                        @click="markSelectedItemsAsChosenStatus('NotStarted')"
                      >
                        <span class="align-middle">
                          Mark as
                          <b-badge
                            pill
                            :variant="`light-${resolveTaskStatusVariant('NotStarted')}`"
                            class="text-capitalize"
                          >
                            {{ splitCamelCaseWords("NotStarted") }}
                          </b-badge>
                        </span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click="markSelectedItemsAsChosenStatus('InProgress')"
                      >
                        <span class="align-middle">
                          Mark as
                          <b-badge
                            pill
                            :variant="`light-${resolveTaskStatusVariant('InProgress')}`"
                            class="text-capitalize"
                          >
                            {{ splitCamelCaseWords("InProgress") }}
                          </b-badge>
                        </span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click="markSelectedItemsAsChosenStatus('Complete')"
                      >
                        <span class="align-middle">
                          Mark as
                          <b-badge
                            pill
                            :variant="`light-${resolveTaskStatusVariant('Complete')}`"
                            class="text-capitalize"
                          >
                            {{ splitCamelCaseWords("Complete") }}
                          </b-badge>
                        </span>
                      </b-dropdown-item>
                      <b-dropdown-item @click="$refs.exportConfirmModal.show()">
                        <span class="align-middle">
                          <b-badge pill variant="warning" class="text-capitalize">
                            {{ splitCamelCaseWords("Export") }}
                          </b-badge>
                        </span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-if="isDeletedSelected"
                        @click="deleteOrRestoreTasks(selectedItems, true)"
                      >
                        <span class="align-middle">
                          <b-badge pill variant="info" class="text-capitalize">
                            {{ splitCamelCaseWords("Restore") }}
                          </b-badge>
                        </span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-if="!isOnlyDeletedSelected"
                        @click="deleteOrRestoreTasks(selectedItems, false)"
                      >
                        <span class="align-middle">
                          <b-badge pill variant="danger" class="text-capitalize">
                            {{ splitCamelCaseWords("Delete") }}
                          </b-badge>
                        </span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </transition>

                <b-button
                  class="d-inline-flex align-items-center"
                  variant="primary"
                  @click="editRecord(null)"
                >
                  <feather-icon icon="PlusIcon" class="mr-50" />
                  <span class="text-nowrap">New Task</span>
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loading && tasksToDisplay.length === 0" class="d-flex justify-content-center py-5">
        <b-spinner type="grow" variant="primary" label="Loading..." />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="tasksToDisplay.length === 0"
        class="text-center py-4 dark-muted-text font-weight-medium"
      >
        {{ emptyText }}
      </div>

      <!-- Grouped by Category View -->
      <div v-else-if="groupBy === 'Category'">
        <!-- Metric column headers -->
        <div class="category-metrics-header-row d-flex align-items-center justify-content-between px-2 py-75 border-bottom font-small-3 font-weight-bold dark-muted-text">
          <div class="metrics-header-left">Category</div>
          <div class="metrics-header-right d-flex align-items-center">
            <span class="metric-col metric-late text-right">Late</span>
            <span class="metric-col metric-due-14 text-right">Due, next 14 days</span>
            <span class="metric-col metric-due-30 text-right">Due, days 15–30</span>
            <span class="metric-col metric-done text-right">Done, last 14 days</span>
          </div>
        </div>

        <!-- All categories aggregate row -->
        <div class="all-categories-row d-flex align-items-center justify-content-between px-2 py-1 border-bottom">
          <div class="font-weight-bold text-body">All categories</div>
          <div class="d-flex align-items-center">
            <span :class="['metric-col', 'metric-late', 'text-right', 'font-weight-bold', allCategoriesMetrics.late > 0 ? 'text-danger' : 'dark-muted-text']">
              {{ allCategoriesMetrics.late }}
            </span>
            <span class="metric-col metric-due-14 text-right font-weight-bold text-body">
              {{ allCategoriesMetrics.dueNext14 }}
            </span>
            <span class="metric-col metric-due-30 text-right font-weight-bold text-body">
              {{ allCategoriesMetrics.dueDays15To30 }}
            </span>
            <span :class="['metric-col', 'metric-done', 'text-right', 'font-weight-bold', allCategoriesMetrics.doneLast14 > 0 ? 'text-success' : 'dark-muted-text']">
              {{ allCategoriesMetrics.doneLast14 }}
            </span>
          </div>
        </div>

        <!-- Category Accordions -->
        <div
          v-for="(group, groupIndex) in groupedCategories"
          :key="group.id"
          class="category-group-section"
        >
          <!-- Accordion Header -->
          <div
            class="category-group-header d-flex align-items-center justify-content-between"
            :class="{ 'is-first': groupIndex === 0 }"
            @click="toggleCategory(group.id)"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                :icon="isCategoryCollapsed(group.id) ? 'ChevronRightIcon' : 'ChevronDownIcon'"
                size="18"
                class="mr-50 text-body cursor-pointer flex-shrink-0"
              />
              <span class="category-prefix-badge mr-50">{{ group.prefix }}</span>
              <span class="category-title font-weight-bold mr-50 text-body">{{ group.description }}</span>
              <span class="category-count dark-muted-text font-small-3 font-weight-medium">
                {{ group.items.length }} upcoming
              </span>
            </div>
            <div class="d-flex align-items-center">
              <span :class="['metric-col', 'metric-late', 'text-right', 'font-weight-bold', getCategoryMetrics(group).late > 0 ? 'text-danger' : 'dark-muted-text']">
                {{ getCategoryMetrics(group).late }}
              </span>
              <span class="metric-col metric-due-14 text-right font-weight-bold text-body">
                {{ getCategoryMetrics(group).dueNext14 }}
              </span>
              <span class="metric-col metric-due-30 text-right font-weight-bold text-body">
                {{ getCategoryMetrics(group).dueDays15To30 }}
              </span>
              <span :class="['metric-col', 'metric-done', 'text-right', 'font-weight-bold', getCategoryMetrics(group).doneLast14 > 0 ? 'text-success' : 'dark-muted-text']">
                {{ getCategoryMetrics(group).doneLast14 }}
              </span>
            </div>
          </div>

          <!-- Category Collapsible Content -->
          <b-collapse :visible="!isCategoryCollapsed(group.id)">
            <task-table
              :id="`category-task-table-${group.id}`"
              :ref="`refTaskListTable_${group.id}`"
              :items="getDisplayedCategoryItems(group)"
              :fields="tableColumns"
              :child-fields="tableColumns"
              :hover="true"
              :no-local-sorting="true"
              :busy="loading"
              :sort-by.sync="sortBy"
              :is-sort-dir-desc.sync="isSortDirDesc"
              :selected-items="selectedItems"
              :is-all-selected="
                group.items.length !== 0 &&
                group.items.every((t) => isItemSelected(t))
              "
              :mark-as-options="markAsOptions"
              :task-due-date-display-format="TASK_DUE_DATE_DISPLAY_FORMAT"
              :current-route-name="currentRouteName"
              :show-bottom-row="false"
              :is-child-table="false"
              @row-clicked="displayRecord"
              @select-all-change="selectOrUnselectAllCategoryRows($event, group)"
              @select-row-change="selectOrUnselectRow"
              @edit-record="editRecord"
              @delete-or-restore-tasks="deleteOrRestoreTasks"
              @update-task="updateTask"
              @update:sort-by="sortBy = $event"
              @update:sort-desc="isSortDirDesc = $event"
            />

            <!-- Preview: Show N more -->
            <div
              v-if="isCategoryInPreview(group)"
              class="show-more-row text-center py-50 border-top cursor-pointer font-weight-medium"
              @click="expandCategoryFully(group.id)"
            >
              <feather-icon icon="ChevronDownIcon" size="14" class="mr-25" />
              Show {{ group.items.length - 2 }} more
            </div>

            <!-- Full view footer: Showing X of X upcoming and Link to open category in its own list -->
            <div
              v-if="group.items.length > 0"
              class="category-table-footer d-flex align-items-center justify-content-between px-2 py-50 border-top font-small-2 dark-muted-text"
            >
              <span>
                Showing {{ isCategoryInPreview(group) ? 2 : group.items.length }} of {{ group.items.length }} upcoming
              </span>
              <a
                href="#"
                class="category-filter-link text-primary font-weight-medium"
                @click.prevent="openCategoryFilter(group)"
              >
                Open {{ group.description }} in its own list
              </a>
            </div>
          </b-collapse>
        </div>

        <!-- Summary Footer for Grouped View -->
        <div class="px-2 py-1 border-top">
          <span class="dark-muted-text font-small-3 font-weight-medium">
            {{ tasksToDisplay.length }} upcoming {{ tasksToDisplay.length === 1 ? 'task' : 'tasks' }} in {{ groupedCategories.length }} {{ groupedCategories.length === 1 ? 'category' : 'categories' }}
          </span>
        </div>
      </div>

      <!-- Flat Table (Group by: None) -->
      <div v-else class="task-list-table">
        <task-table
          id="taskListTable"
          ref="refTaskListTable"
          :items="tasksToDisplayWithChildRows"
          :fields="tableColumns"
          :child-fields="tableColumns"
          :hover="true"
          :no-local-sorting="true"
          :busy="loading"
          :empty-text="paginationLoading ? 'Loading...' : emptyText"
          :sort-by.sync="sortBy"
          :is-sort-dir-desc.sync="isSortDirDesc"
          :selected-items="selectedItems"
          :is-all-selected="
            tasksToDisplay.length !== 0 &&
            tasksToDisplay.length === selectedItems.length
          "
          :mark-as-options="markAsOptions"
          :task-due-date-display-format="TASK_DUE_DATE_DISPLAY_FORMAT"
          :current-route-name="currentRouteName"
          :show-bottom-row="true"
          :is-child-table="false"
          @row-clicked="displayRecord"
          @select-all-change="selectOrUnselectAllRows"
          @select-row-change="selectOrUnselectRow"
          @edit-record="editRecord"
          @delete-or-restore-tasks="deleteOrRestoreTasks"
          @update-task="updateTask"
          @update:sort-by="sortBy = $event"
          @update:sort-desc="isSortDirDesc = $event"
          show-bottom-row
        >
          <template #bottom-row>
            <b-th colspan="99">
              <TaskListPagination
                :loading="paginationLoading"
                :value="currentPage"
                @input="pageChange"
                :per-page-options="perPageOptions"
                :per-page.sync="perPage"
                :has-next-page="hasNextPage"
              />
            </b-th>
          </template>
        </task-table>
      </div>

      <!-- Hint marked as center -->
      <div
        class="text-center mt-2 mb-2"
        v-if="groupBy === 'None' && !hasNextPage && tasksToDisplay.length > 0"
      >
        <b-card-text class="dark-muted-text">
          <span class="dark-muted-text"
            >Only showing tasks until
            {{
              formatDate(tasksToDisplay[tasksToDisplay.length - 1].dueDate)
            }}</span
          >
        </b-card-text>
      </div>
    </div>

    <BModal
      ref="exportConfirmModal"
      title="Please Confirm"
      centered
      ok-title="Yes"
      @ok="markSelectedItemsAsChosenStatus('Exported')"
      @close="markAsExported = false"
      @cancel="markAsExported = false"
    >
      <div>
        <p class="text-center">
          Are you sure that you want to export these tasks?
        </p>
        <div class="d-flex justify-content-center align-items-center">
          <BFormCheckbox v-model="markAsExported"
            >Mark as Exported</BFormCheckbox
          >
          <span
            v-b-tooltip.hover
            title="This option is similar to marking a task as Complete; the task will become Inactive and will be removed from Late status. Choose this when you no longer want to track the Exported tasks."
            class="inline-block"
            style="margin-left: 4px"
          >
            <FeatherIcon icon="HelpCircleIcon" size="16" />
          </span>
        </div>
      </div>
    </BModal>
  </div>
</template>

<script>
import TaskTable from "./TaskTable.vue"
import TaskListPagination from "./TaskListPagination.vue"
import {
  generateFutureTasks,
  TASK_DUE_DATE_DISPLAY_FORMAT,
  isValidTaskFilter,
  removeExtraFieldsFromTask,
  formatPersistedTask,
  formatProcessesForTaskGeneration,
  compareTaskHandles,
  computeValidDatesMap,
} from "pbshared"
import { areArraysEqual, findInReverse, xLog } from "../../utils/object-util"
import {
  BRow,
  BCol,
  BModal,
  BButton,
  BTable,
  // BTooltip,
  BSpinner,
  BCardText,
  VBTooltip,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BFormCheckbox,
  BPagination,
  BTh,
  BCollapse,
} from "bootstrap-vue"
import Ripple from "vue-ripple-directive"
import { debounce } from "vue-debounce"
import { mapState, mapActions, mapMutations } from "vuex"
// eslint-disable-next-line import/extensions
import { formatDate, formatDateForUser } from "@/utils/date-utils.js"
import { DAYS_BETWEEN_DEFAULT_INTERVAL } from "@/utils/constants"
import { selectPosition } from "@core/mixins/ui/select"
import EntityFilter from "@/views/shared/Filter.vue"
import { TASK_ENTITY_FIELD_SUGGESTIONS } from "@/utils/entity-filter"
import FilterTasksByDateModal from "@/views/task/FilterTasksByDateModal.vue"
import vSelect from "vue-select"
import moment from "moment-timezone"
import TaskAdd from "./TaskAdd.vue"
import _ from "lodash"
import Vue from "vue"

export default {
  components: {
    TaskTable,
    TaskListPagination,
    vSelect,
    // custom components
    TaskAdd,
    EntityFilter,
    FilterTasksByDateModal,

    // Bootstrap components
    BRow,
    BCol,
    BButton,
    BTable,
    // BTooltip,
    BSpinner,
    // eslint-disable-next-line vue/no-unused-components
    BCardText,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BFormCheckbox,
    BPagination,
    BTh,
    BCollapse,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },
  mixins: [selectPosition],
  data() {
    const userType =
      this.$store?.state?.user?.currentUser?.userType ||
      this.$store?.state?.auth?.role
    const isAdminOrManager =
      userType?.toLowerCase() === "admin" ||
      userType?.toLowerCase() === "manager"
    const defaultShowMine = !isAdminOrManager

    return {
      groupBy: "Category",
      categoryStates: {},
      completedCountsByCategory: {},
      markAsExported: false,
      pageMeta: [],
      init: false,
      processesForTasks: [],
      validDates: null,
      paginationLoading: true,
      loading: true,
      tasksToSkip: {},
      hasNextPage: true,
      oldFilters: [],
      TASK_DUE_DATE_DISPLAY_FORMAT,
      isAddNewTaskSidebarActive: false,
      isAddNewTaskSidebarInDisplayMode: false,
      activeRecord: null,
      activeRecordIdx: null,
      searchFilter: {
        taskType: null,
      },
      perPageOptions: [10, 25, 50, 100],
      perPage: 25,
      currentPage: 1,
      lastDynamicTaskEveryPage: [],
      lastDisplayedTaskEveryPage: [],
      dbTasksToSkipPerPage: [],
      searchQuery: [],
      selectedQueries: [],
      sortBy: "dueDate",
      isSortDirDesc: false,
      generatedTasksForAllUsers: [],
      // from: new Date(Date.now()),
      // from: '',
      from: new Date(
        new Date(Date.now()).setFullYear(new Date(Date.now()).getFullYear() - 1)
      ),
      to: new Date(
        new Date(Date.now()).setDate(
          new Date(Date.now()).getDate() + DAYS_BETWEEN_DEFAULT_INTERVAL
        )
      ),
      tasksToDisplay: [],
      tasksToDisplayLoading: false,
      maxIterations: 26, // Loads at maximum for one year
      iteration: 0,
      isEditing: false,
      showPagination: false,

      // FOR AUTO SUGGEST
      filteringFunctions: ["!task.deleted && task.status !== 'Complete'"], // used for filtering generated tasks using javascript
      TASK_ENTITY_FIELD_SUGGESTIONS,

      // For Mark as "status" functionality
      markAsOptions: ["NotStarted", "InProgress", "Complete"],

      loadTasksSelectedOption: { label: "Next 2 Weeks", value: 5 },
      loadTasksDays: 5,
      loadTasksOptions: [
        { label: "Next 5 Days", value: 5 },
        { label: "Next 2 Weeks", value: 14 },
        { label: "Next 1 Month", value: 30 },
        { label: "Next 2 Months", value: 60 },
        { label: "Next 6 Months", value: 180 },
        { label: "Next 1 Year", value: 365 },
      ],
      // For selecting table rows
      selectedItems: [],

      // preventing to call the refetch function on page first render
      refetching: false,
      showMine: defaultShowMine,
    }
  },
  computed: {
    emptyText() {
      return `No matching records found between ${formatDateForUser(
        this.from
      )} to ${formatDateForUser(this.to)}`
    },
    processHash() {
      return _.keyBy(this.processesForTasks, "processHandle")
    },
    tasksToDisplayWithChildRows() {
      const tasks = []

      this.tasksToDisplay.forEach((task) => {
        tasks.push(task)
        if (task.is_group && task._showChildren && task.tasks) {
          task.tasks.forEach((childTask) => {
            tasks.push({ ...childTask, is_child: true })
          })
        }
      })

      return tasks
    },
    tableColumns() {
      const isGrouped = this.groupBy === "Category"
      return [
        {
          key: "select",
          label: "",
          thClass: "select-th",
          tdClass: "select-td",
        },
        {
          key: "processId",
          label: "ID",
          thClass: "id-th",
          tdClass: "id-td",
          sortable: !isGrouped,
        },
        {
          key: "name",
          label: "Task",
          thClass: "name-th",
          tdClass: "name-td",
          sortable: !isGrouped,
        },
        {
          key: "notes",
          label: "",
          thClass: "notes-th",
          tdClass: "notes-td",
          sortable: false,
        },
        {
          key: "dueDate",
          label: "Due",
          thClass: "due-th",
          tdClass: "due-td",
          sortable: !isGrouped,
        },
        {
          key: "userId",
          label: "Assigned to",
          thClass: "assigned-th",
          tdClass: "assigned-td",
          hide: this.showMine,
        },
        {
          key: "role",
          label: "Role",
          thClass: "role-th",
          tdClass: "role-td",
        },
        {
          key: "updated_at",
          label: "Updated",
          thClass: "updated-th",
          tdClass: "updated-td",
          sortable: !isGrouped,
        },
        {
          key: "status",
          label: "Status",
          thClass: "status-th",
          tdClass: "status-td",
        },
        {
          key: "actions",
          label: "",
          thClass: "actions-th",
          tdClass: "actions-td",
          sortable: false,
        },
      ].filter((i) => !i.hide)
    },
    groupedCategories() {
      const list = this.tasksToDisplay || []
      const groupsMap = {}
      const groupsOrder = []

      list.forEach((item) => {
        const cat = item.cat || (item.process && item.process.cat)
        const catId = cat && cat.id ? String(cat.id) : "uncategorized"
        const prefix = cat && cat.prefix ? cat.prefix : "OTHER"
        const description =
          cat && (cat.description || cat.prefix)
            ? cat.description || cat.prefix
            : "Uncategorized"

        if (!groupsMap[catId]) {
          groupsMap[catId] = {
            id: catId,
            prefix,
            description,
            items: [],
          }
          groupsOrder.push(groupsMap[catId])
        }
        groupsMap[catId].items.push(item)
      })

      return groupsOrder
    },
    allCategoriesMetrics() {
      let late = 0
      let dueNext14 = 0
      let dueDays15To30 = 0
      let doneLast14 = 0

      this.groupedCategories.forEach((group) => {
        const m = this.getCategoryMetrics(group)
        late += m.late
        dueNext14 += m.dueNext14
        dueDays15To30 += m.dueDays15To30
        doneLast14 += m.doneLast14
      })

      return {
        late,
        dueNext14,
        dueDays15To30,
        doneLast14,
      }
    },
    ...mapState("user", ["currentUser"]),
    ...mapState("task", [
      "tasks",
      "currentUserTasks",
      // 'fieldValues',
    ]),
    ...mapState("task", { taskIsLoading: "isLoading" }),
    // ...mapState('process', { processIsLoading: 'isLoading' }),
    // ...mapState('user', ['users', 'allUsers']),
    // ...mapState('user', { userIsLoading: 'isLoading' }),
    ...mapState("filter", ["filtersAccessibleToCurrentUser"]),
    isAdminOrManager() {
      const userType =
        this.currentUser?.userType || this.$store.state.auth?.role
      return (
        userType?.toLowerCase() === "admin" ||
        userType?.toLowerCase() === "manager"
      )
    },
    defaultShowMine() {
      return !this.isAdminOrManager
    },
    tasksOffset() {
      const lastPageMeta = this.pageMeta[this.pageMeta.length - 1]
      return lastPageMeta?.dbTasksUsedUntilNow || 0
    },
    isDeletedSelected() {
      return this.selectedItems.some((t) => t.deleted)
    },
    isOnlyDeletedSelected() {
      return this.selectedItems.every((t) => t.deleted)
    },
    tasksReqParams() {
      const isGrouped = this.groupBy === "Category"
      return {
        limit: isGrouped ? 500 : this.perPage,
        sortBy: "dueDate",
        isDesc: false,
        paginated: !isGrouped,
        offset: isGrouped ? 0 : this.tasksOffset,
        totalCount: this.currentPage === 1 ? undefined : this.tasks.totalCount,
        q: this.selectedQueries,
        getTasksToSkip: this.currentPage === 1,
        onlyMine: this.showMine,
      }
    },
    paginationMeta() {
      let tasksToPaginate = { nodes: [], totalCount: 0 }
      if (!this.showMine) tasksToPaginate = this.tasks
      else tasksToPaginate = this.currentUserTasks
      return {
        from:
          this.perPage * (this.currentPage - 1) +
          (tasksToPaginate.nodes.length ? 1 : 0),
        to:
          this.perPage * (this.currentPage - 1) + tasksToPaginate.nodes.length,
        of: tasksToPaginate.totalCount,
      }
    },
    queryParams() {
      return {
        taskType: this.searchFilter.taskType,
        q: this.selectedQueries,
        offset: this.perPage * (this.currentPage - 1),
        limit: this.perPage,
        sortBy: this.sortBy,
        isDesc: this.isSortDirDesc,
        from: this.from,
        to: this.to,
        paginated: this.showPagination,
      }
    },
    dateRange: {
      get() {
        if (!this.from) {
          return `null to ${formatDate(this.to)}`
        }
        return `${formatDate(this.from)} to ${formatDate(this.to)}`
      },
      set(newValue) {
        const intervalLimits = newValue.split(" to ")
        if (intervalLimits[0] !== "null")
          [this.from, this.to] = intervalLimits.map((e) => new Date(e))
        else {
          this.from = new Date(Date.now())
          this.to = new Date(intervalLimits[1])
        }
      },
    },
    isSelectAllChecked: {
      get() {
        return this.tasksToDisplay.length === this.selectedItems.length
      },
      set(newValue) {
        this.selectOrUnselectAllRows(newValue)
      },
    },
    currentRouteName() {
      return this.$route.name
    },
  },
  watch: {
    groupBy(newVal) {
      this.currentPage = 1
      this.fetchTasks(true)
      const query = { ...this.$route.query }
      if (query.groupBy !== newVal) {
        query.groupBy = newVal
        this.$router.replace({ query }).catch(() => {})
      }
    },
    perPage() {
      this.generateTasks(true)
    },
    async dateRange(value) {
      const intervalLimits = value.split(" to ")
      // eslint-disable-next-line prefer-const
      let [fromValue, toValue] = intervalLimits.map((e) => new Date(e))
      this.from = fromValue
      this.to = toValue
      this.generateTasks(true)
    },
    selectedQueries(newVal) {
      const getValidFilters = (filters) =>
        filters.filter((f) => isValidTaskFilter(f))

      const string = this.selectedQueries[0]
      if (string) {
        if (string.includes("all")) {
          this.queryParams.sortBy = "updated_at"
          this.sortBy = "updated_at"
          this.isSortDirDesc = true
        } else if (string.includes("active") || string.includes("late")) {
          this.queryParams.sortBy = "dueDate"
          this.sortBy = "dueDate"
          this.isSortDirDesc = false
        } else if (string.includes("complete") || string.includes("delete")) {
          this.queryParams.sortBy = "updated_at"
          this.sortBy = "updated_at"
          this.isSortDirDesc = true
        }
      } else {
        this.queryParams.sortBy = "updated_at"
        this.sortBy = "updated_at"
        this.isSortDirDesc = true
      }

      const validFilters = getValidFilters(newVal)

      // 🔍 Extract old and new role, category, and label filters
      const extractProcessRelationFilters = (filters) =>
        filters.filter(
          (f) =>
            f.startsWith("role=") ||
            f.startsWith("role!=") ||
            f.startsWith("category=") ||
            f.startsWith("category!=") ||
            f.startsWith("labels=") ||
            f.startsWith("labels!=") ||
            f.startsWith("label=") ||
            f.startsWith("label!=")
        )

      const oldRelFilters = extractProcessRelationFilters(this.oldFilters || [])
      const newRelFilters = extractProcessRelationFilters(validFilters)

      const processFiltersChanged = !areArraysEqual(oldRelFilters, newRelFilters)

      if (processFiltersChanged) {
        this.processesForTasks = []
      }

      if (!areArraysEqual(this.oldFilters, validFilters)) {
        this.selectedItems = []
        this.fetchTasks(true)
      }

      this.oldFilters = validFilters
    },

    showMine(newVal) {
      this.selectedItems = []
      this.fetchTasks(true, true)
      const query = { ...this.$route.query }
      const mineStr = newVal ? "true" : "false"
      if (query.mine !== mineStr) {
        query.mine = mineStr
        this.$router.replace({ query }).catch(() => {})
      }
    },
    currentUser: {
      handler() {
        if (this.$route.query.mine === undefined) {
          this.showMine = this.defaultShowMine
        }
      },
    },
    $route: {
      handler(to) {
        const query = to.query

        if (query.groupBy && this.groupBy !== query.groupBy) {
          this.groupBy = query.groupBy
        }

        if (query.mine !== undefined) {
          const isMine = query.mine === "true" || query.mine === true
          if (this.showMine !== isMine) {
            this.showMine = isMine
          }
        } else if (this.showMine !== this.defaultShowMine) {
          this.showMine = this.defaultShowMine
        }

        let assignQuery = []
        if (query.q) {
          assignQuery = Array.isArray(query.q) ? query.q : [query.q]
        } else {
          assignQuery = ["is:active"]
        }

        if (!areArraysEqual(this.selectedQueries, assignQuery)) {
          this.selectedQueries = assignQuery
        }
      },
      deep: true,
    },

  },
  async created() {
    this.$store.commit("process/RESET_PROCESSES")
    const { q, mine, groupBy } = this.$route.query

    if (groupBy) {
      this.groupBy = groupBy
    }

    if (mine !== undefined) {
      this.showMine = mine === "true" || mine === true
    } else {
      this.showMine = this.defaultShowMine
    }

    if (q) {
      if (Array.isArray(q)) {
        this.selectedQueries = q
      } else {
        this.selectedQueries = [q]
      }
    } else {
      this.selectedQueries = ["is:active"]
    }

    if (!this.showMine) {
      // this.perPage = 10
      this.showPagination = true
    }

    this.suggestions = TASK_ENTITY_FIELD_SUGGESTIONS
  },
  methods: {
    ...mapActions("role", ["getForTaskGeneration"]),
    ...mapActions("process", ["getProcessesForTasks"]),
    ...mapActions("task", ["getTasks", "getCurrentUserTasks"]),
    formatPersistedTask(t) {
      return formatPersistedTask(this.$tenantTimezone, this.processHash)(t)
    },
    pageChange(page) {
      const isGoingBack = page < this.currentPage
      this.currentPage = page
      this.generateTasks(false, isGoingBack)
    },
    ...mapActions("filter", ["getFiltersAccessibleToCurrentUser"]),
    refetchData: debounce(async function cb() {
      await this.fetchTasks()
    }, "300ms"),
    onLoadOptionSelect(value) {
      this.loadTasksDays = value
    },
    updateLocalTaskList(idx, task) {
      if (idx === null) return
      this.tasksToDisplay.splice(idx, 1, task)
    },
    async generateTasks(
      reset = false,
      isGoingBack = false,
      removedTaskIndices = [],
      refresh = false,
      dbTasks = null
    ) {
      this.paginationLoading = true

      if (refresh) {
        this.pageMeta.pop()
      }

      let resetInternal = reset
      if (removedTaskIndices.length) {
        removedTaskIndices.forEach((idx) => {
          const t = this.tasksToDisplay[idx]
          Vue.set(this.tasksToSkip, t.init_handle.split("_")[0], t)
        })
      }

      if (resetInternal === true) {
        this.currentPage = 1
        this.tasksToDisplay = []
        this.hasNextPage = true
        this.pageMeta = []
        this.$store.commit("task/RESET_TASKS")
      }

      if (isGoingBack) {
        this.pageMeta = this.pageMeta.slice(0, -2)
      }

      try {
        if (dbTasks) {
          this.$store.commit("task/SET_TASKS", dbTasks)
        } else if (
          this.tasksOffset < this.tasks.totalCount ||
          resetInternal ||
          refresh
        ) {
          console.time("tasks request")
          await this.$store.dispatch("task/getTasks", this.tasksReqParams)
          console.timeEnd("tasks request")
        } else {
          this.$store.commit("task/NO_TASKS")
        }

        this.init = true

        if (this.currentPage === 1 && !removedTaskIndices.length) {
          this.tasksToSkip = _.keyBy(
            this.tasks.tasksToSkip,
            (t) => t.init_handle.split("_")[0]
          )
        }

        const isGrouped = this.groupBy === "Category"
        const lastPageMeta = this.pageMeta[this.pageMeta.length - 1]
        const { tasks, tasksUsedFromDB, hasNextPage } = generateFutureTasks(
          true,
          this.validDates,
          this.$tenantTimezone,
          this.processesForTasks,
          [...this.tasks.nodes],
          moment().tz(this.$tenantTimezone),
          isGrouped ? 500 : this.perPage,
          this.selectedQueries,
          lastPageMeta?.lastDisplayedTask,
          this.tasksToSkip,
          isGrouped
            ? moment().tz(this.$tenantTimezone).add(30, "days")
            : undefined
        )

        if (hasNextPage === undefined) {
          this.hasNextPage =
            this.tasks.totalCount > this.tasksOffset + this.perPage
        } else {
          this.hasNextPage = hasNextPage
        }

        this.tasksToDisplay = tasks

        const tl = findInReverse(tasks, (t) => t.isDynamic)
        const tf = tasks[0]
        this.pageMeta.push({
          dbTasksUsedUntilNow: tasksUsedFromDB + this.tasksOffset,
          lastDisplayedTask: tl
            ? {
                ...tl,
                dueDate: tl.dueDate,
                j: tl.is_group
                  ? findInReverse(tl.tasks, (t) => t.isDynamic)?.j
                  : tl.j,
                isDynamic: tl.isDynamic,
              }
            : undefined,
          firstDisplayedTask: {
            dueDate: tf?.dueDate,
          },
        })
      } catch (error) {
        console.error(error)
      }

      // This is necessary for showing correct current page
      // If we remove this, the displayed page changes before the next tasks request is completed
      this.$nextTick(() => {
        this.paginationLoading = false
      })
    },
    async fetchTasks(reset = false, totalReset = false) {
      this.loading = true

      if (reset) {
        this.currentPage = 1
        this.tasksToDisplay = []
        this.hasNextPage = true
        this.pageMeta = []
        this.selectedItems = []
        this.$store.commit("task/RESET_TASKS")
      }

      try {
        let dbTasks = null
        if (!this.processesForTasks.length || totalReset) {
          console.time("processes request")
          const res = await this.getProcessesForTasks(this.tasksReqParams)
          if (this._isDestroyed) return
          console.timeEnd("processes request")

          console.time("processes formatting")
          this.processesForTasks = formatProcessesForTaskGeneration(
            res.processes.nodes
          )
          this.validDates = computeValidDatesMap(
            this.processesForTasks,
            this.$tenantTimezone
          )
          console.timeEnd("processes formatting")
          dbTasks = res.tasks
        }
        if (this._isDestroyed) return
        console.time("task generation")

        await Promise.all([
          this.generateTasks(reset, false, [], false, dbTasks),
          this.fetchCompletedCounts(),
        ])
        if (this._isDestroyed) return
        console.timeEnd("task generation")
      } catch (err) {
        console.error(err)
      }

      this.loading = false

      return
    },
    getCategoryState(catId) {
      return this.categoryStates[catId] || "preview"
    },
    isCategoryCollapsed(catId) {
      return this.getCategoryState(catId) === "collapsed"
    },
    isCategoryInPreview(group) {
      return (
        this.getCategoryState(group.id) === "preview" && group.items.length > 2
      )
    },
    getDisplayedCategoryItems(group) {
      const state = this.getCategoryState(group.id)
      if (state === "collapsed") return []
      const baseItems =
        state === "preview" ? group.items.slice(0, 2) : group.items

      const itemsWithChildren = []
      baseItems.forEach(task => {
        itemsWithChildren.push(task)
        if (task.is_group && task._showChildren && task.tasks) {
          task.tasks.forEach(childTask => {
            itemsWithChildren.push({ ...childTask, is_child: true })
          })
        }
      })
      return itemsWithChildren
    },
    toggleCategory(catId) {
      const current = this.getCategoryState(catId)
      if (current === "collapsed") {
        this.$set(this.categoryStates, catId, "full")
      } else {
        this.$set(this.categoryStates, catId, "collapsed")
      }
    },
    expandCategoryFully(catId) {
      this.$set(this.categoryStates, catId, "full")
    },
    expandAllCategories() {
      this.groupedCategories.forEach(group => {
        this.$set(this.categoryStates, group.id, "full")
      })
    },
    collapseAllCategories() {
      this.groupedCategories.forEach(group => {
        this.$set(this.categoryStates, group.id, "collapsed")
      })
    },
    openCategoryFilter(group) {
      const existingQueries = (this.selectedQueries || []).filter(
        (q) => !q.startsWith("category=") && !q.startsWith("category!=")
      )
      const catPrefix = group.prefix || "OTHER"
      const newQueries = [...existingQueries, `category=${catPrefix}`]
      const query = {
        ...this.$route.query,
        groupBy: "None",
        q: newQueries,
      }
      this.$router.replace({ query }).catch(() => {})
    },
    getCategoryMetrics(group) {
      const tenantTz = this.$tenantTimezone || "America/Chicago"
      const today = moment().tz(tenantTz).startOf("day")
      const in14Days = today.clone().add(14, "days").endOf("day")
      const in30Days = today.clone().add(30, "days").endOf("day")

      let late = 0
      let dueNext14 = 0
      let dueDays15To30 = 0

      const allTasks = group.items.flatMap(item =>
        item.is_group && item.tasks ? item.tasks : [item]
      )

      allTasks.forEach(task => {
        if (
          task.deleted ||
          task.status === "Complete" ||
          task.status === "Exported"
        ) {
          return
        }

        const taskDueDate = moment(task.dueDate).tz(tenantTz).startOf("day")

        if (task.is_late || taskDueDate.isBefore(today)) {
          late += 1
        } else if (taskDueDate.isSameOrBefore(in14Days)) {
          dueNext14 += 1
        } else if (taskDueDate.isSameOrBefore(in30Days)) {
          dueDays15To30 += 1
        }
      })

      const doneLast14 = this.completedCountsByCategory[group.id] || 0

      return {
        late,
        dueNext14,
        dueDays15To30,
        doneLast14,
      }
    },
    selectOrUnselectAllCategoryRows(value, group) {
      if (value) {
        group.items.forEach(item => {
          if (item.is_group && item.tasks) {
            item.tasks.forEach(child => {
              if (!this.selectedItems.some(e => e.handle === child.handle)) {
                this.selectedItems.push(child)
              }
            })
          } else if (
            !this.selectedItems.some(e => e.handle === item.handle)
          ) {
            this.selectedItems.push(item)
          }
        })
      } else {
        const handlesToRemove = new Set(
          group.items.flatMap(item =>
            item.is_group && item.tasks
              ? item.tasks.map(t => t.handle)
              : [item.handle]
          )
        )
        this.selectedItems = this.selectedItems.filter(
          item => !handlesToRemove.has(item.handle)
        )
      }
    },
    async fetchCompletedCounts() {
      try {
        const counts = await this.$store.dispatch(
          "task/getCompletedTasksCountByCategory",
          {
            onlyMine: this.showMine,
          }
        )
        const countsMap = {}
        if (counts && Array.isArray(counts)) {
          counts.forEach(c => {
            const key = c.categoryId ? String(c.categoryId) : "uncategorized"
            countsMap[key] = (countsMap[key] || 0) + c.count
          })
        }
        this.completedCountsByCategory = countsMap
      } catch (e) {
        console.error("Failed to fetch completed counts", e)
      }
    },
    resolveTaskStatusVariant(status) {
      if (status === "NotStarted") return "secondary"
      if (status === "Complete") return "success"
      if (status === "InProgress") return "info"
      if (status === "Exported") return "warning"
      return "primary"
    },
    editRecord(activeRecord, idx) {
      if (activeRecord) {
        this.activeRecord = activeRecord
        this.activeRecordIdx = idx
      }
      this.isAddNewTaskSidebarActive = true
      this.isAddNewTaskSidebarInDisplayMode = false
      this.isEditing = true
    },
    displayRecord(activeRecord, idx) {
      if (activeRecord.is_group) {
        this.$set(
          activeRecord,
          "_showChildren",
          !activeRecord._showChildren
        )
        return
      }

      this.editRecord(activeRecord, idx)
    },
    deleteOrRestoreTasks(t, restore = false) {
      if (!t || !t.length) return

      const isSingleTask = t.length === 1
      const tasks = isSingleTask
        ? t
        : t.filter((t) => (restore ? !!t.deleted : !t.deleted))
      const firstTask = tasks[0]
      if (!tasks.length) return

      const getMessage = () => {
        if (isSingleTask) {
          return `Are you sure that you want to ${
            firstTask.deleted ? "restore" : "delete"
          } this task?`
        }
        return `Are you sure that you want to ${
          restore ? "restore" : "delete"
        } ${tasks.length} tasks?`
      }

      const getModalOptions = () => {
        if (isSingleTask) {
          return {
            title: "Please Confirm",
            size: "sm",
            okVariant: firstTask.deleted ? "info" : "danger",
            okTitle: firstTask.deleted ? "Yes" : "Delete",
            cancelTitle: firstTask.deleted ? "No" : "Cancel",
            cancelVariant: "outline-secondary",
            hideHeaderClose: false,
            centered: true,
          }
        }
        return {
          title: "Please Confirm",
          size: "sm",
          okVariant: restore ? "info" : "danger",
          okTitle: restore ? "Restore" : "Delete",
          cancelTitle: "Cancel",
          cancelVariant: "outline-secondary",
          hideHeaderClose: false,
          centered: true,
        }
      }

      this.$bvModal
        .msgBoxConfirm(getMessage(), getModalOptions())
        .then((confirmation) => {
          if (!confirmation) return

          const updatedTasks = tasks.map((task) => ({
            ...task,
            deleted: restore ? false : true,
          }))

          this.updateTasks(updatedTasks)

          if (!isSingleTask) {
            this.isEditing = false
            this.selectedItems = []
          }
        })
    },

    async updateTasks(records, data) {
      const itemsToUpdate = records.map((item) =>
        removeExtraFieldsFromTask({
          ...item,
          ...data,
        })
      )

      try {
        const updatedTasks = await this.$store
          .dispatch("task/updateTasks", itemsToUpdate)
          .then((res) => res.data.updateTasks.nodes)

        const tasks = updatedTasks.map((newTask) => {
          const activeRecordIdx = this.tasksToDisplay.findIndex((t) =>
            compareTaskHandles(newTask.init_handle, t.init_handle)
          )
          const oldTask = this.tasksToDisplay[activeRecordIdx]

          return {
            oldTask,
            newTask,
            idx: activeRecordIdx,
            updatedTask: this.formatPersistedTask(
              _.merge(_.clone(oldTask), _.clone(newTask))
            ),
          }
        })

        this.postTaskUpdateActions(tasks)

        return tasks
      } catch (error) {
        console.error("Error bulk updating tasks", error)
      }
    },
    async markSelectedItemsAsChosenStatus(status) {
      const isExported = status === "Exported"
      this.isEditing = true

      if (isExported) {
        this.showPopup()

        if (!this.markAsExported) {
          this.downloadTabSeparatedSheet(
            this.selectedItems.map((i) => this.formatPersistedTask(i))
          )
          this.isEditing = false
          this.selectedItems = []
          return
        }
      }

      try {
        const updatedTasks = await this.updateTasks(this.selectedItems, {
          status,
        })

        if (isExported) {
          this.downloadTabSeparatedSheet(updatedTasks.map((t) => t.updatedTask))
        }

        this.selectedItems = []
        this.resetIsEditingValue()
        this.markAsExported = false
      } catch (error) {
        this.$swal.close()
        console.error("Error bulk updating tasks", error)
      }
    },
    async updateTask(activeRecord, idx, updatedFields) {
      this.isEditing = true

      try {
        const activeRecordSanitized = removeExtraFieldsFromTask({
          ...activeRecord,
          ...updatedFields,
        })

        const res = await this.$store.dispatch(
          `task/${
            activeRecordSanitized.id ? "updateTask" : "updateFutureTask"
          }`,
          activeRecordSanitized
        )

        const newOrUpdatedTask =
          res.data[activeRecordSanitized.id ? "updateTask" : "updateFutureTask"]

        this.postTaskUpdateActions([
          {
            oldTask: activeRecord,
            newTask: newOrUpdatedTask,
            idx,
          },
        ])
      } catch (error) {
        console.error(error)
      }

      this.resetIsEditingValue()
    },
    postTaskUpdateActions(tasks) {
      const removedIndices = []

      tasks.forEach(({ oldTask, newTask, idx }) => {
        const task = this.formatPersistedTask({ ...oldTask, ...newTask })
        if (task?.status === "Complete" || task.deleted) {
          if (idx !== null && idx !== undefined && idx !== -1) {
            removedIndices.push(idx)
          }
        }

        this.pageMeta = this.pageMeta.map((meta, metaIdx) => {
          if (metaIdx === this.pageMeta.length - 1) return meta

          if (meta.firstDisplayedTask?.dueDate?.isAfter(task.dueDate)) {
            return {
              ...meta,
              dbTasksUsedUntilNow: meta.dbTasksUsedUntilNow + 1,
            }
          }

          return meta
        })
      })

      this.generateTasks(false, false, removedIndices, true)
      this.fetchCompletedCounts()
    },
    formatDate,
    formatDateForUser,
    incrementIteration() {
      this.iteration += 1
    },
    loadTwoMoreWeeksClicked() {
      let lastInterval
      if (this.loadTasksDays === 5) {
        lastInterval = new Date(
          this.to.getFullYear(),
          this.to.getMonth(),
          this.to.getDate() + (this.loadTasksDays || 5)
        )
      } else if (this.loadTasksDays === 14) {
        lastInterval = new Date(
          this.to.getFullYear(),
          this.to.getMonth(),
          this.to.getDate() + (this.loadTasksDays || 14)
        )
      } else if (this.loadTasksDays === 30) {
        lastInterval = new Date(
          this.to.getFullYear(),
          this.to.getMonth() + 1,
          this.to.getDate()
        )
      } else if (this.loadTasksDays === 60) {
        lastInterval = new Date(
          this.to.getFullYear(),
          this.to.getMonth() + 2,
          this.to.getDate()
        )
      } else if (this.loadTasksDays === 180) {
        lastInterval = new Date(
          this.to.getFullYear(),
          this.to.getMonth() + 6,
          this.to.getDate()
        )
      } else if (this.loadTasksDays === 365) {
        lastInterval = new Date(
          this.to.getFullYear() + 1,
          this.to.getMonth(),
          this.to.getDate()
        )
      } else {
        lastInterval = new Date(
          this.to.getFullYear(),
          this.to.getMonth(),
          this.to.getDate() + (this.loadTasksDays || 14)
        )
      }
      if (this.from)
        this.dateRange = `${formatDate(this.from)} to ${formatDate(
          lastInterval
        )}`
      else this.dateRange = `null to ${formatDate(lastInterval)}`
    },
    async oldestDate() {
      return new Date(Date.now())
    },
    async startDate() {
      const date = await this.oldestDate(this.tasks)
      return date > Date.now() ? this.from : date
    },
    resetIsEditingValue() {
      this.$nextTick(() => {
        this.isEditing = false
      })
    },
    formattedDueDate(date) {
      return moment(date).format("MMM DD YYYY")
    },
    splitCamelCaseWords(text) {
      const result = text.replace(/([A-Z])/g, " $1")
      return result.charAt(0).toUpperCase() + result.slice(1)
    },

    // for multi-select feature
    selectOrUnselectRow($event, record) {
      if ($event) {
        this.selectedItems.push(record)
      } else {
        const recordToDeleteIndex = this.selectedItems.findIndex(
          (e) => e.handle === record.handle
        )
        this.selectedItems.splice(recordToDeleteIndex, 1)
      }
    },
    isItemSelected(record) {
      if (!record) return false
      if (record.is_group && record.tasks) {
        return (
          record.tasks.length > 0 &&
          record.tasks.every((child) =>
            this.selectedItems.some((e) => e.handle === child.handle)
          )
        )
      }
      return !!this.selectedItems.find((e) => e.handle === record.handle)
    },
    selectAllRows() {
      this.selectedItems = this.tasksToDisplay.flatMap((task) =>
        task.is_group && task.tasks ? task.tasks : [task]
      )
    },
    clearSelected() {
      this.selectedItems = []
    },
    selectOrUnselectAllRows(value) {
      if (value) this.selectAllRows()
      else this.clearSelected()
    },
    deleteSelectedRecords() {
      this.isEditing = true
      const selectedItemsUpdated = []
      for (let i = 0; i < this.selectedItems.length; i += 1) {
        const selectedItemUpdated = {
          ...this.selectedItems[i],
          deleted: true,
        }
        delete selectedItemUpdated.updated_at
        delete selectedItemUpdated.assignee
        delete selectedItemUpdated.process
        selectedItemsUpdated.push(selectedItemUpdated)
      }
      this.$store
        .dispatch("task/updateTasks", selectedItemsUpdated)
        .then(() => {
          this.tasksToDisplay = this.removeUnion(
            this.tasksToDisplay,
            this.selectedItems,
            "id"
          )
          this.resetIsEditingValue()
          this.selectedItems = []
          this.fetchCompletedCounts()
        })
    },
    showPopup() {
      this.$swal.fire({
        title: "Exporting",
        text: "Exporting the selected tasks… just a moment, please.",
        icon: "info",
        showCancelButton: false,
        showConfirmButton: false,
      })
    },
    downloadTabSeparatedSheet(data) {
      // Convert the list of objects to a tab-separated string
      const tabSeparatedHeaderData = [
        `${"assignee_name"}\t${"assignee_id"}\tdescription\tdueDate\tis_late\tname\tprocessHandle\tstatus\tupdated_at\tprocess_link`,
      ]
      const tabSeparatedContentData = data.map((item) => {
        return `${item.assignee.name}\t${item.assignee.peopleHandle || ""}\t${
          item.description || ""
        }\t${item.dueDate}\t${item.is_late}\t${item.name}\t${
          item.process.processHandle
        }\t${item.status}\t${item.updated_at || ""}\t${
          process.env.VUE_APP_FE_URL
        }/processes?id=${item.processId}&compact=true`
      })

      const tabSeparatedData = [
        ...tabSeparatedHeaderData,
        ...tabSeparatedContentData,
      ].join("\n")
      // Create a Blob from the string
      const blob = new Blob([tabSeparatedData], { type: "text/plain" })

      // Create a temporary URL for the Blob
      const url = URL.createObjectURL(blob)

      // Create an anchor element for downloading
      const a = document.createElement("a")
      a.href = url
      const currentDate = moment() // Get the current date and time

      // Format the date in the required format
      const formattedDate = currentDate.format("YYYY-MM-DD-HH-mm-ss")
      a.download = `task_data_${formattedDate}.tsv` // Set the desired file name
      // Trigger a click event on the anchor to start the download
      a.click()

      // Clean up by revoking the URL
      URL.revokeObjectURL(url)
      this.$swal.close()
    },
    removeUnion(arr1, arr2, propertyToCompare) {
      const set2 = new Set(arr2.map((item) => item[propertyToCompare]))
      const result = arr1.filter((item) => !set2.has(item[propertyToCompare]))
      return result
    },

    onDateSelect(dateRange) {
      this.selectedQueries = ["is:active", `date=${dateRange}`]
    },
    onClearFilter() {
      if (this.$route.query.d && this.$route.query.d === "org") {
        this.$router.replace({ query: { q: this.$route.query.q } })
        this.showPagination = false
      }
    },
  },
}
</script>

<style lang="scss">
[dir]
  .table.b-table.table-hover
  > tbody
  > tr.child-row:hover
  > .table-b-table-default {
  background-image: none;
  background-color: #5c5c5c !important;
}

.global-task-list {
  .select-edit {
    width: 81.88px !important;
    max-width: 81.88px !important;
    padding: 10.8px !important;
  }

  .select-edit-head {
    margin-left: 33px;
    justify-content: center;
  }
}

.my-task-list {
  .select-edit {
    width: 50px !important;
    max-width: 50px !important;
    padding: 10.8px !important;
  }

  .select-edit-head {
    margin-left: 5px;
  }
}

.task-list {
  @import "../../@core/scss/vue/libs/vue-select";

  .task-list-table {
    overflow-x: auto;
  }

  .per-page-selector {
    width: 90px;
  }
}

.task-table {
  margin-bottom: 0;

  thead th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    border-top: none;
    border-bottom: 1px solid #ebe9f1;
    padding: 0.75rem 1rem !important;
    background-color: #ffffff;
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #fafafc !important;
    }

    td {
      padding: 0.75rem 1rem !important;
      vertical-align: middle !important;
      border-top: 1px solid #ebe9f1;
    }
  }

  .select-th,
  .select-td {
    width: 40px !important;
    max-width: 40px !important;
    padding-right: 0 !important;
  }

  .id-th,
  .id-td {
    width: 75px !important;
    max-width: 85px !important;
  }

  .name-th,
  .name-td {
    width: 1% !important;
    white-space: nowrap !important;
  }

  .notes-th,
  .notes-td {
    width: 40px !important;
    max-width: 45px !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    text-align: center;
  }

  .due-th,
  .due-td {
    width: 125px !important;
    max-width: 140px !important;
    white-space: nowrap;
  }

  .assigned-th,
  .assigned-td {
    width: 160px !important;
    max-width: 180px !important;
  }

  .role-th,
  .role-td {
    width: 90px !important;
    max-width: 110px !important;
  }

  .updated-th,
  .updated-td {
    width: 85px !important;
    max-width: 100px !important;
  }

  .status-th,
  .status-td {
    width: 110px !important;
    max-width: 125px !important;
  }

  .actions-th,
  .actions-td {
    width: 40px !important;
    max-width: 40px !important;
    text-align: right;
    padding-right: 1.25rem !important;
  }
}

.dark-muted-text {
  color: #5e5873 !important;
}

.dark-layout {
  .dark-muted-text {
    color: #d0d2d6 !important;
  }
  .category-metrics-header-row {
    background-color: #283046 !important;
    color: #d0d2d6 !important;
    border-color: #3b4253 !important;
  }
  .all-categories-row {
    background-color: #1e2433 !important;
    border-color: #3b4253 !important;
    color: #d0d2d6 !important;
  }
  .category-group-header {
    background-color: rgba(25, 58, 154, 0.2) !important;
    border-color: rgba(25, 58, 154, 0.35) !important;
    &:hover {
      background-color: rgba(25, 58, 154, 0.3) !important;
    }
  }
  .category-title {
    color: #d0d2d6 !important;
  }
  .category-count {
    color: #d0d2d6 !important;
  }
  .show-more-row {
    background-color: #161d31 !important;
    color: #7367f0 !important;
    border-color: #3b4253 !important;
    &:hover {
      background-color: #283046 !important;
      color: #9087f4 !important;
    }
  }
  .category-table-footer {
    background-color: #283046 !important;
    border-color: #3b4253 !important;
    color: #d0d2d6 !important;
  }
  .container-card {
    background-color: #283046 !important;
  }
  .task-table {
    thead th {
      background-color: #283046 !important;
      border-color: #3b4253 !important;
      color: #b4b7bd !important;
    }
    tbody tr td {
      border-color: #3b4253 !important;
    }
    tbody tr:hover {
      background-color: #161d31 !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@import "../../@core/scss/vue/libs/vue-select";

.container-card {
  background-color: white;
  border: none;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08);
  border-radius: 0.428rem;
  overflow: hidden;
}

.per-page-selector {
  width: 90px;
}

.toolbar-container {
  row-gap: 0.5rem;
}

.toolbar-left {
  min-width: 0;
}

.filter-wrapper {
  min-width: 260px;
  flex: 1 1 auto;
}

.category-metrics-header-row {
  background-color: #ffffff;
  color: #6e6b7b;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #ebe9f1;
}

.all-categories-row {
  background-color: #fafafc;
  border-bottom: 1px solid #ebe9f1;
  font-size: 0.92rem;
}

.category-group-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: rgba(25, 58, 154, 0.08);
  border-top: 1px solid rgba(25, 58, 154, 0.15);
  border-bottom: 1px solid rgba(25, 58, 154, 0.15);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(25, 58, 154, 0.14);
  }

  &.is-first {
    border-top: none;
  }
}

.category-prefix-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #193a9a;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  min-width: 28px;
  text-align: center;
}

.category-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2e384d;
}

.category-count {
  font-size: 0.82rem;
  font-weight: 500;
  color: #5e5873;
}

.metric-col {
  display: inline-block;
  white-space: nowrap;
}

.metric-late {
  width: 75px;
  min-width: 75px;
}

.metric-due-14 {
  width: 155px;
  min-width: 155px;
}

.metric-due-30 {
  width: 145px;
  min-width: 145px;
}

.metric-done {
  width: 145px;
  min-width: 145px;
  padding-right: 0.5rem;
}

.show-more-row {
  background-color: #fafafc;
  color: #193a9a;
  padding: 0.5rem 1rem;
  cursor: pointer;
  user-select: none;
  font-size: 0.82rem;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: rgba(25, 58, 154, 0.08);
    color: #162a56;
    text-decoration: underline;
  }
}

.category-table-footer {
  background-color: #fafafc;
}

.category-filter-link {
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline !important;
  }
}

.logo {
  height: 17px;
  width: 17px;
  padding: 1px;
  margin-right: 10px;
}

.table-responsive {
  overflow-x: visible !important;
}
</style>
